/**
 * DataTable's state, as TanStack Store atoms.
 *
 * WHY ATOMS AND NOT THE OTHER TWO WAYS. v9 gives three ways to own table state and they must not
 * be mixed: external atoms, `options.state` paired with `on<Slice>Change` callbacks, and the
 * table's own internal base atoms. Precedence is `options.atoms[key]` > `options.state[key]` >
 * the internal atom, which is exactly how two of them silently cancel each other out. We use
 * atoms everywhere because the URL is the source of truth for filters and sort: a screen that
 * owns the URL owns the atom, writes to it on a route change, and the table reads through it with
 * no second copy of the state to keep in step (docs/ui/03-components.md).
 */
import { createAtom } from '@tanstack/store';
import type { Atom } from '@tanstack/store';
import type {
  ColumnSizingState,
  ColumnVisibilityState,
  PaginationState,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table';

export interface DataTableAtoms {
  sorting: Atom<SortingState>;
  pagination: Atom<PaginationState>;
  rowSelection: Atom<RowSelectionState>;
  columnVisibility: Atom<ColumnVisibilityState>;
  columnSizing: Atom<ColumnSizingState>;
}

export interface DataTableInitialState {
  sorting?: SortingState;
  pagination?: PaginationState;
  rowSelection?: RowSelectionState;
  columnVisibility?: ColumnVisibilityState;
  columnSizing?: ColumnSizingState;
}

/** The default page size. 25 is the first of DataTable's three offered sizes. */
export const DEFAULT_PAGE_SIZE = 25;

/**
 * Build one table's worth of state atoms.
 *
 * Call this in the screen that owns the URL and hand the result to `<DataTable atoms={…} />`;
 * DataTable makes its own set when you do not, which is the same model with a narrower owner.
 */
export function createDataTableAtoms(initial: DataTableInitialState = {}): DataTableAtoms {
  return {
    sorting: createAtom<SortingState>(initial.sorting ?? []),
    pagination: createAtom<PaginationState>(
      initial.pagination ?? { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE },
    ),
    rowSelection: createAtom<RowSelectionState>(initial.rowSelection ?? {}),
    columnVisibility: createAtom<ColumnVisibilityState>(initial.columnVisibility ?? {}),
    columnSizing: createAtom<ColumnSizingState>(initial.columnSizing ?? {}),
  };
}

/**
 * Serialise sort for the URL as a single `id.asc` / `id.desc` string, never as a raw state array.
 * Empty and default-valued parameters are DELETED from the URL rather than written as blanks, so
 * a table at rest has a clean address; every filter write uses `replace: true` so filtering does
 * not fill the back button (docs/ui/03-components.md).
 */
export function serialiseSort(sorting: SortingState): string | undefined {
  const first = sorting[0];
  if (!first) {
    return undefined;
  }
  return `${first.id}.${first.desc ? 'desc' : 'asc'}`;
}

/** The inverse of `serialiseSort`. An unparseable value yields no sort rather than a throw. */
export function parseSort(value: string | null | undefined): SortingState {
  if (!value) {
    return [];
  }
  const separator = value.lastIndexOf('.');
  if (separator <= 0) {
    return [];
  }
  const id = value.slice(0, separator);
  const direction = value.slice(separator + 1);
  if (direction !== 'asc' && direction !== 'desc') {
    return [];
  }
  return [{ id, desc: direction === 'desc' }];
}
